import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Pagination,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  AccessTime, 
  Computer,
  Smartphone,
  Tv,
  Build,
  AttachMoney,
  Help,
  MoreVert,
  VideoCall,
  Message,
  Send,
  Close as CloseIcon,
  Add as AddIcon 
} from '@mui/icons-material';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PageWrapper = styled(Box)({
  backgroundColor: '#f2f7ff',
  minHeight: '100vh',
  padding: '24px 0'
});

const JobCard = styled(Paper)({
  padding: '20px',
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  '&:hover': {
    borderColor: '#108a00',
    cursor: 'pointer'
  }
});

const RequestButton = styled(Button)({
  backgroundColor: '#108a00',
  color: 'white',
  borderRadius: '20px',
  padding: '6px 16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0c6b00'
  }
});

const FilterSection = styled(Box)({
  marginBottom: '20px',
  padding: '16px',
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e0e0e0'
});

const ProblemCard = styled(Paper)({
  padding: '20px',
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }
});

const CategoryTab = styled(Tab)({
  textTransform: 'none',
  minWidth: 100,
  '&.Mui-selected': {
    color: '#108a00',
  }
});

const PostCard = styled(Card)({
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }
});

const LiveAssistanceCard = styled(Paper)({
  padding: '20px',
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
});

const StyledCard = styled(Card)({
  maxWidth: 800,
  margin: '24px auto',
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
});

const EditorWrapper = styled(Box)({
  '& .ql-container': {
    minHeight: '200px',
    fontSize: '16px',
  },
  '& .ql-editor': {
    minHeight: '200px',
  },
});

const SidebarCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  padding: theme.spacing(3),
}));

const categories = [
  'Mobile Devices',
  'Computers',
  'Smart Home',
  'Gaming',
  'TV & Audio',
  'Network & Internet',
  'Software',
  'Other',
];

const computerProblems = [
  {
    author: {
      name: "Alex Thompson",
      avatar: "", // Add avatar URL
    },
    title: "CPU Overheating Issues with Gaming PC",
    description: "My CPU temperature is reaching 95°C while gaming. I've noticed significant performance drops and occasional shutdowns. Running an Intel i9 with stock cooler.",
    category: "Processor",
    budget: "$50-100",
    timePosted: "1 hour ago",
    responses: 12,
    severity: "High"
  },
  {
    author: {
      name: "Sarah Chen",
      avatar: "", // Add avatar URL
    },
    title: "RAM Compatibility Issues After Upgrade",
    description: "System unstable after adding new RAM sticks. Getting frequent BSODs and memory errors. Current setup: 32GB (4x8GB) DDR4.",
    category: "Memory",
    budget: "$30-50",
    timePosted: "2 hours ago",
    responses: 8,
    severity: "Medium"
  },
  {
    author: {
      name: "Mike Wilson",
      avatar: "", // Add avatar URL
    },
    title: "SSD Performance Degradation",
    description: "My Samsung 970 EVO Plus NVMe SSD's read/write speeds have dropped significantly. Running benchmark shows 50% slower than advertised speeds.",
    category: "Storage",
    budget: "$75-150",
    timePosted: "3 hours ago",
    responses: 15,
    severity: "Medium"
  },
  {
    author: {
      name: "Emily Rodriguez",
      avatar: "", // Add avatar URL
    },
    title: "GPU Artifacting During Gaming",
    description: "RTX 3080 showing strange colored lines and artifacts in games. Temperature seems normal but performance is degrading.",
    category: "Graphics",
    budget: "$100-200",
    timePosted: "4 hours ago",
    responses: 20,
    severity: "High"
  },
  {
    author: {
      name: "David Kim",
      avatar: "", // Add avatar URL
    },
    title: "Motherboard Not Detecting New GPU",
    description: "Recently installed RTX 4070 Ti not being detected. PCIe slot might be faulty. Motherboard: ASUS ROG STRIX B550-F.",
    category: "Motherboard",
    budget: "$80-150",
    timePosted: "5 hours ago",
    responses: 16,
    severity: "High"
  },
  {
    author: {
      name: "James Wilson",
      avatar: "",
    },
    title: "PSU Making Clicking Sounds",
    description: "850W Corsair PSU making intermittent clicking sounds under load. System occasionally shuts down during gaming.",
    category: "Power Supply",
    budget: "$100-200",
    timePosted: "6 hours ago",
    responses: 14,
    severity: "High"
  },
  {
    author: {
      name: "Lisa Chen",
      avatar: "",
    },
    title: "CPU Fan Speed Fluctuation",
    description: "CPU fan randomly speeds up and slows down without temperature changes. Using Noctua NH-D15 cooler.",
    category: "Cooling",
    budget: "$40-80",
    timePosted: "7 hours ago",
    responses: 9,
    severity: "Medium"
  },
];

const trendingIssues = [
  {
    issue: "DDR5 RAM Compatibility Issues",
    trend: "↑ 65% this week",
    severity: "High"
  },
  {
    issue: "GPU Driver Conflicts",
    trend: "↑ 45% this week",
    severity: "Medium"
  },
  {
    issue: "CPU Thermal Throttling",
    trend: "↑ 38% this week",
    severity: "High"
  }
];

const mostAskedQuestions = [
  {
    question: "How to properly apply thermal paste?",
    category: "CPU",
    responses: 256
  },
  {
    question: "Best practices for GPU overclocking",
    category: "GPU",
    responses: 189
  },
  {
    question: "Diagnosing RAM stability issues",
    category: "Memory",
    responses: 145
  }
];

const Feed2 = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [showPostForm, setShowPostForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [page, setPage] = useState(1);
  const problemsPerPage = 20;

  // Calculate pagination
  const totalPages = Math.ceil(computerProblems.length / problemsPerPage);
  const startIndex = (page - 1) * problemsPerPage;
  const endIndex = startIndex + problemsPerPage;
  const currentProblems = computerProblems.slice(startIndex, endIndex);

  const problems = [
    {
      title: "iPhone 13 Screen Flickering Issue",
      description: "My iPhone 13's screen started flickering after the latest iOS update. Anyone experienced similar issues?",
      category: "SMARTPHONE",
      budget: "$50-100",
      timePosted: "2 hours ago",
      urgency: "High",
      status: "Open",
      responses: 3,
      author: {
        name: "John Doe",
        avatar: "/path-to-avatar.jpg",
      }
    },
    // ... other problems
  ];

  const experts = [
    {
      name: "Sarah Chen",
      specialty: "Mobile Expert",
      rating: 4.9,
      price: "$45/hour",
      availability: "Available Now",
      avatar: "/path-to-avatar.jpg"
    },
    {
      name: "Mike Wilson",
      specialty: "TV Specialist",
      rating: 4.8,
      price: "$50/hour",
      availability: "Available in 1h",
      avatar: "/path-to-avatar.jpg"
    }
  ];

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log({ title, content, category, price });
    setShowPostForm(false); // Close form after submission
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Nav1 />
      
      <Box sx={{ backgroundColor: '#f2f7ff', py: 3, flex: 1 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Main Feed */}
            <Grid item xs={12} md={8}>
              {/* Post Problem Button */}
              {!showPostForm && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowPostForm(true)}
                  sx={{
                    backgroundColor: '#108a00',
                    '&:hover': { backgroundColor: '#0c6b00' },
                    mb: 3,
                  }}
                >
                  Post Your Problem
                </Button>
              )}

              {/* Category Tabs */}
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab icon={<Computer />} label="PC" />
                <Tab icon={<Smartphone />} label="Smartphone" />
                <Tab icon={<Tv />} label="TV" />
                <Tab icon={<Build />} label="Electronics" />
              </Tabs>

              {/* Post Problem Form */}
              {showPostForm && (
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Post Your Problem
                      </Typography>
                      <IconButton onClick={() => setShowPostForm(false)}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box component="form" onSubmit={handleSubmit}>
                      <TextField
                        fullWidth
                        label="Problem Title"
                        variant="outlined"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        sx={{ mb: 3 }}
                        placeholder="Describe your problem briefly"
                      />

                      <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={category}
                            label="Category"
                            onChange={(e) => setCategory(e.target.value)}
                          >
                            {categories.map((cat) => (
                              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Problem Description
                      </Typography>
                      <EditorWrapper sx={{ mb: 3 }}>
                        <ReactQuill
                          theme="snow"
                          value={content}
                          onChange={setContent}
                          modules={modules}
                          placeholder="Describe your problem in detail..."
                        />
                      </EditorWrapper>

                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        alignItems: 'center',
                        mb: 3,
                        mt: 5 
                      }}>
                        <FormControl sx={{ width: '200px' }}>
                          <TextField
                            label="Budget"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormControl>

                        <Button
                          variant="contained"
                          size="large"
                          type="submit"
                          startIcon={<Send />}
                          sx={{
                            backgroundColor: '#108a00',
                            '&:hover': {
                              backgroundColor: '#0c6b00',
                            },
                            px: 4,
                            py: 1.5,
                          }}
                        >
                          Post Problem
                        </Button>
                      </Box>

                      <Typography variant="caption" color="textSecondary">
                        By posting, you agree to our Terms of Service and Privacy Policy.
                        Your problem will be visible to our expert community.
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              )}

              {/* Problems Feed with Pagination */}
              {currentProblems.map((problem, index) => (
                <PostCard key={index}>
                  <CardHeader
                    avatar={
                      <Avatar src={problem.author.avatar}>{problem.author.name[0]}</Avatar>
                    }
                    title={problem.author.name}
                    subheader={problem.timePosted}
                  />
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#108a00', mb: 1 }}>
                      {problem.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {problem.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip 
                        label={problem.category} 
                        size="small"
                        sx={{ backgroundColor: '#f2f7ff', color: '#108a00' }}
                      />
                      <Chip 
                        label={problem.budget} 
                        size="small"
                        icon={<AttachMoney sx={{ fontSize: 16 }} />}
                        sx={{ backgroundColor: '#e4ebe4' }}
                      />
                      <Chip 
                        label={`${problem.responses} responses`} 
                        size="small"
                        sx={{ backgroundColor: '#e4ebe4' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Help />}
                        sx={{
                          backgroundColor: '#108a00',
                          '&:hover': { backgroundColor: '#0c6b00' }
                        }}
                      >
                        Help Solve
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{
                          color: '#108a00',
                          borderColor: '#108a00',
                          '&:hover': {
                            borderColor: '#0c6b00',
                            backgroundColor: 'rgba(16, 138, 0, 0.04)'
                          }
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </CardContent>
                </PostCard>
              ))}

              {/* Pagination Controls */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Stack spacing={2}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#108a00',
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#108a00 !important',
                        color: 'white',
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Grid>

            {/* Live Assistance Sidebar */}
            <Grid item xs={12} md={4}>
              <SidebarCard elevation={0}>
                <Typography variant="h6" sx={{ mb: 2, color: '#108a00' }}>
                  Live Assistance
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: '#5e6d55' }}>
                  Get instant help from our verified experts
                </Typography>
                
                {experts.map((expert, index) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Avatar src={expert.avatar}>{expert.name[0]}</Avatar>
                      <Box>
                        <Typography variant="subtitle1">{expert.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {expert.specialty}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#108a00' }}>
                          {expert.price} • {expert.availability}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<VideoCall />}
                        fullWidth
                        sx={{
                          backgroundColor: '#108a00',
                          '&:hover': { backgroundColor: '#0c6b00' }
                        }}
                      >
                        Video Call
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Message />}
                        fullWidth
                        sx={{
                          color: '#108a00',
                          borderColor: '#108a00',
                          '&:hover': {
                            borderColor: '#0c6b00',
                            backgroundColor: 'rgba(16, 138, 0, 0.04)'
                          }
                        }}
                      >
                        Chat
                      </Button>
                    </Box>
                    {index < experts.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </SidebarCard>

              {/* Updated Most Asked Questions */}
              <SidebarCard elevation={0}>
                <Typography variant="h6" sx={{ mb: 2, color: '#108a00' }}>
                  Most Asked Questions
                </Typography>
                {mostAskedQuestions.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {item.question}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={item.category} 
                        size="small"
                        sx={{ backgroundColor: '#f2f7ff', color: '#108a00' }}
                      />
                      <Chip 
                        label={`${item.responses} responses`} 
                        size="small"
                        sx={{ backgroundColor: '#e4ebe4' }}
                      />
                    </Box>
                    {index < 2 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </SidebarCard>

              {/* Updated Trending Issues */}
              <SidebarCard elevation={0}>
                <Typography variant="h6" sx={{ mb: 2, color: '#108a00' }}>
                  Trending Issues
                </Typography>
                {trendingIssues.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {item.issue}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#108a00' }}>
                        {item.trend}
                      </Typography>
                      <Chip 
                        label={item.severity} 
                        size="small"
                        sx={{ 
                          backgroundColor: item.severity === 'High' ? '#fff2f2' : '#f2f7ff',
                          color: item.severity === 'High' ? '#d32f2f' : '#108a00'
                        }}
                      />
                    </Box>
                    {index < 2 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </SidebarCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default Feed2; 